import React, { useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Radio } from 'antd';
import classNames from 'classnames';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';

import { ActionVisibilityValueType } from 'utils/adl-services';
import Pficon from 'components/Pficon';
import { QUOTA_MODE } from 'data/quotaModes';

import useModalLogicTitleAndDescription from './hooks/useModalLogicTitleAndDescription';
import { getDataTestId } from './utils';
import { DATA_TEST_ID, HIDE_ANSWER_LOGIC_MODE } from './constants';
import messages, { QUOTA_REMOVAL_MESSAGE } from './messages';
import './style.less';

function ConfirmModal({
  valueType,
  nodeFeatures = [],
  nodeType,
  quotaModeToBeCleared = QUOTA_MODE.NONE,
  title,
  description,
  onCancel,
  onSubmit,
  onHideAnswerOnly,
}) {
  const [isOpen, setIsOpen] = useState(true);
  const closeModal = useCallback(() => setIsOpen(false), []);
  const hasFeaturesEnabled = nodeFeatures.length > 0;
  const quotaRemovalMessage = QUOTA_REMOVAL_MESSAGE[quotaModeToBeCleared];
  const { logicTitle, logicDescription, featuresDescription } =
    useModalLogicTitleAndDescription(valueType, nodeType, hasFeaturesEnabled);

  // Optional RES-12286 path: the researcher picks whether to keep or delete
  // the affected rules; onSubmit stays the delete-rules confirm.
  const isThreeWayHide = typeof onHideAnswerOnly === 'function';
  const [logicMode, setLogicMode] = useState(HIDE_ANSWER_LOGIC_MODE.KEEP_RULES);
  const isDeleteRules = logicMode === HIDE_ANSWER_LOGIC_MODE.REMOVE_RULES;
  const onChangeLogicMode = useCallback(
    (event) => setLogicMode(event.target.value),
    [],
  );

  let submitLabel = messages.confirmHide.confirmTextLegacy;
  if (isThreeWayHide) {
    submitLabel = isDeleteRules
      ? messages.confirmHide.confirmText
      : messages.confirmHide.hideOnlyText;
  }
  const onClickSubmit =
    isThreeWayHide && !isDeleteRules ? onHideAnswerOnly : onSubmit;
  const bodyContent = isThreeWayHide ? (
    <FormattedMessage
      {...(messages.confirmHide.body[valueType] ||
        messages.confirmHide.body.answer)}
    />
  ) : (
    logicDescription
  );

  const renderLogicModeOption = (mode, testId, title, description) => {
    const isSelected = logicMode === mode;
    const optionTestId = getDataTestId(valueType, testId);

    return (
      <Radio
        value={mode}
        className={classNames(
          'flex flex-items-start w-full margin-none border-radius-level-1 text-weight-regular unconditional-hide-answer-logic-mode__option',
          {
            'border-grey-10 background-grey-3': isSelected,
            'border-grey-6': !isSelected,
          },
        )}
        data-testid={optionTestId}
      >
        <div
          className="color-grey-10 text-weight-500 unconditional-hide-answer-logic-mode__title"
          data-testid={`${optionTestId}-title`}
        >
          <FormattedMessage {...title} />
        </div>
        <div
          className="color-grey-9 unconditional-hide-answer-logic-mode__description"
          data-testid={`${optionTestId}-description`}
        >
          <FormattedMessage {...description} />
        </div>
      </Radio>
    );
  };

  return (
    <Modal
      mask
      destroyOnClose
      visible={isOpen}
      onCancel={closeModal}
      afterClose={onCancel}
      footer={null}
      width={480}
      closeIcon={
        <Pficon
          type="close"
          className="color-grey-10 color-grey-8-hover transition-normal text-size-primary"
          data-testid={getDataTestId(
            valueType,
            DATA_TEST_ID.CONFIRM_MODAL_CROSS,
          )}
        />
      }
      className="unconditional-hide-answer-confirm-modal"
      data-testid={getDataTestId(valueType, DATA_TEST_ID.CONFIRM_MODAL)}
    >
      <div className="flex flex-col gap-lg padding-lg color-grey-10">
        <h3
          className="flex flex-align-items--center text-weight-bold margin-none unconditional-hide-answer-confirm-modal__title"
          data-testid={getDataTestId(
            valueType,
            DATA_TEST_ID.CONFIRM_MODAL_TITLE,
          )}
        >
          {!isThreeWayHide && (
            <ExclamationCircleFilled className="margin-right-xs color-yellow-6" />
          )}
          {title ||
            (isThreeWayHide ? (
              <FormattedMessage
                {...(messages.confirmHide.title[valueType] ||
                  messages.confirmHide.title.answer)}
              />
            ) : (
              logicTitle
            ))}
        </h3>

        <div className="flex flex-col gap-md unconditional-hide-answer-confirm-modal__content">
          {quotaRemovalMessage && (
            <p className="margin-none">
              <FormattedMessage {...quotaRemovalMessage} />
            </p>
          )}
          <p
            className="margin-none"
            data-testid={getDataTestId(
              valueType,
              DATA_TEST_ID.CONFIRM_MODAL_BODY,
            )}
          >
            {description || bodyContent}
          </p>

          {isThreeWayHide && (
            <Radio.Group
              value={logicMode}
              onChange={onChangeLogicMode}
              className="flex flex-col gap-xs w-full unconditional-hide-answer-logic-mode"
              data-testid={getDataTestId(
                valueType,
                DATA_TEST_ID.CONFIRM_MODAL_OPTIONS,
              )}
            >
              {renderLogicModeOption(
                HIDE_ANSWER_LOGIC_MODE.KEEP_RULES,
                DATA_TEST_ID.CONFIRM_MODAL_OPTION_KEEP_RULES,
                messages.confirmHide.keepRules.title,
                messages.confirmHide.keepRules.description,
              )}
              {renderLogicModeOption(
                HIDE_ANSWER_LOGIC_MODE.REMOVE_RULES,
                DATA_TEST_ID.CONFIRM_MODAL_OPTION_DELETE_RULES,
                messages.confirmHide.deleteRules.title,
                messages.confirmHide.deleteRules.description,
              )}
            </Radio.Group>
          )}

          {valueType === ActionVisibilityValueType.Node &&
            hasFeaturesEnabled && (
              <Fragment>
                <ul
                  className="margin-none padding-left-lg"
                  style={{ listStyle: 'circle' }}
                >
                  {nodeFeatures.map((feature) => (
                    <li key={feature.id}>
                      <FormattedMessage {...feature} />
                    </li>
                  ))}
                </ul>

                <p className="margin-none">{featuresDescription}</p>
              </Fragment>
            )}
        </div>

        <footer className="flex flex-justify--end flex-wrap gap-md">
          <Button
            className="color-grey-10 color-grey-10-hover border-grey-5 box-shadow-none border-radius-level-1 text-weight-500 unconditional-hide-answer-confirm-modal__button"
            onClick={closeModal}
            data-testid={getDataTestId(
              valueType,
              DATA_TEST_ID.CONFIRM_MODAL_CLOSE,
            )}
          >
            <FormattedMessage {...messages.confirmHide.cancelText} />
          </Button>
          <Button
            className={classNames(
              'box-shadow-none border-radius-level-1 text-weight-500 unconditional-hide-answer-confirm-modal__button',
              {
                'color-grey-1 color-grey-1-hover background-purple-dark-important border-purple-dark-important':
                  isThreeWayHide && !isDeleteRules,
                'color-grey-10 color-grey-10-hover background-yellow-4 border-yellow-4':
                  !isThreeWayHide || isDeleteRules,
              },
            )}
            onClick={onClickSubmit}
            data-testid={getDataTestId(
              valueType,
              isThreeWayHide && !isDeleteRules
                ? DATA_TEST_ID.CONFIRM_MODAL_HIDE_ONLY
                : DATA_TEST_ID.CONFIRM_MODAL_SUBMIT,
            )}
          >
            <FormattedMessage {...submitLabel} />
          </Button>
        </footer>
      </div>
    </Modal>
  );
}

ConfirmModal.propTypes = {
  valueType: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onHideAnswerOnly: PropTypes.func,
  nodeFeatures: PropTypes.array,
  nodeType: PropTypes.number,
  quotaModeToBeCleared: PropTypes.oneOf(Object.values(QUOTA_MODE)),
  // Replace the hide-with-logic copy, for changes other than hiding.
  title: PropTypes.node,
  description: PropTypes.node,
};

export default React.memo(ConfirmModal);
